import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_URL from '../api';
import Layout from '../components/Layout';

export default function Dashboard() {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ customer_id: '', customer_name: '', job_address: '', job_number: '', contract_amount: '' });
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Type-ahead customer picker state
  const [custQuery, setCustQuery] = useState('');
  const [showDrop, setShowDrop] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => { loadJobs(); loadCustomers(); }, []);

  // If we're returning from creating a new customer, reopen the job form
  // with that customer pre-selected.
  useEffect(() => {
    const pending = sessionStorage.getItem('newJobReturn');
    if (pending) {
      try {
        const data = JSON.parse(pending);
        setForm(f => ({ ...f, customer_id: data.customer_id || '', customer_name: data.customer_name || '' }));
        setCustQuery(data.customer_name || '');
        setShowForm(true);
      } catch (e) { console.log(e); }
      sessionStorage.removeItem('newJobReturn');
      loadCustomers();
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const onClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowDrop(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const loadJobs = async () => {
    const res = await axios.get(`${API_URL}/api/jobs`, { headers });
    setJobs(res.data);
  };

  const loadCustomers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customers`, { headers });
      setCustomers(res.data);
    } catch (e) { console.log(e); }
  };

  const createJob = async (e) => {
    e.preventDefault();
    await axios.post(`${API_URL}/api/jobs`, form, { headers });
    resetForm();
    setShowForm(false);
    loadJobs();
  };

  const resetForm = () => {
    setForm({ customer_id: '', customer_name: '', job_address: '', job_number: '', contract_amount: '' });
    setCustQuery('');
  };

  // Filter customers by what's typed
  const matches = custQuery.trim()
    ? customers.filter(c => (c.display_name || '').toLowerCase().includes(custQuery.toLowerCase())).slice(0, 8)
    : [];

  const pickCustomer = (c) => {
    setForm({ ...form, customer_id: c.id, customer_name: c.display_name });
    setCustQuery(c.display_name);
    setShowDrop(false);
  };

  // Hand off to Customers page: remember the typed name + that we want to
  // come back to the job form, then open the new-customer form pre-filled.
  const goCreateCustomer = () => {
    sessionStorage.setItem('createCustomerPrefill', custQuery.trim());
    sessionStorage.setItem('returnToNewJob', '1');
    navigate('/customers');
  };

  const fmt = (n) => '$' + parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  const getMargin = (job) => {
    const revised = parseFloat(job.contract_amount) + parseFloat(job.total_cos || 0);
    const costs = parseFloat(job.total_materials || 0) + parseFloat(job.total_labor || 0);
    if (revised === 0) return '—';
    return ((revised - costs) / revised * 100).toFixed(1) + '%';
  };

  const getBalance = (job) => {
    const revised = parseFloat(job.contract_amount) + parseFloat(job.total_cos || 0);
    return revised - parseFloat(job.total_paid || 0);
  };

  const totalContractValue = jobs.reduce((s, j) => s + parseFloat(j.contract_amount) + parseFloat(j.total_cos || 0), 0);
  const totalCollected = jobs.reduce((s, j) => s + parseFloat(j.total_paid || 0), 0);
  const totalOutstanding = jobs.reduce((s, j) => s + getBalance(j), 0);
  const activeJobs = jobs.filter(j => j.status === 'active').length;

  return (
    <Layout>
      <div style={styles.topbar}>
        <h1 style={styles.pageTitle}>Dashboard</h1>
        <button onClick={() => setShowForm(!showForm)} style={styles.btnPrimary}>+ New Job</button>
      </div>

      <div style={styles.body}>
        {/* KPI Cards */}
        <div style={styles.kpiGrid}>
          <div style={styles.kpiCard}><div style={styles.kpiLabel}>Total Contract Value</div><div style={styles.kpiValue}>{fmt(totalContractValue)}</div></div>
          <div style={styles.kpiCard}><div style={styles.kpiLabel}>Total Collected</div><div style={{...styles.kpiValue, color: '#1E7145'}}>{fmt(totalCollected)}</div></div>
          <div style={styles.kpiCard}><div style={styles.kpiLabel}>Outstanding Balance</div><div style={{...styles.kpiValue, color: '#C0392B'}}>{fmt(totalOutstanding)}</div></div>
          <div style={styles.kpiCard}><div style={styles.kpiLabel}>Active Jobs</div><div style={styles.kpiValue}>{activeJobs}</div></div>
        </div>

        {/* New Job Form */}
        {showForm && (
          <div style={styles.formCard}>
            <h3 style={{margin: '0 0 16px 0', color: '#2E4057'}}>New Job</h3>
            <form onSubmit={createJob} style={styles.formGrid}>

              {/* Type-ahead customer picker */}
              <div style={{position:'relative'}} ref={pickerRef}>
                <input
                  style={{...styles.input, width:'100%', boxSizing:'border-box'}}
                  placeholder="Customer * (type to search)"
                  value={custQuery}
                  onChange={e => {
                    setCustQuery(e.target.value);
                    setShowDrop(true);
                    setForm({ ...form, customer_id: '', customer_name: e.target.value });
                  }}
                  onFocus={() => setShowDrop(true)}
                  required
                />
                {showDrop && custQuery.trim() && (
                  <div style={styles.dropdown}>
                    {matches.map(c => (
                      <div key={c.id} style={styles.dropItem}
                        onMouseDown={() => pickCustomer(c)}>
                        <strong>{c.display_name}</strong>
                        {c.email ? <span style={{color:'#888',fontSize:'12px'}}> · {c.email}</span> : null}
                      </div>
                    ))}
                    {matches.length === 0 && (
                      <div style={{...styles.dropItem, color:'#888'}}>No match found.</div>
                    )}
                    <div style={styles.dropCreate} onMouseDown={goCreateCustomer}>
                      + Create New Customer
                    </div>
                  </div>
                )}
              </div>

              <input style={styles.input} placeholder="Job Address" value={form.job_address} onChange={e => setForm({...form, job_address: e.target.value})} />
              <input style={styles.input} placeholder="Job Number" value={form.job_number} onChange={e => setForm({...form, job_number: e.target.value})} />
              <input style={styles.input} type="number" placeholder="Contract Amount *" value={form.contract_amount} onChange={e => setForm({...form, contract_amount: e.target.value})} required />
              <button style={styles.btnPrimary} type="submit">Create Job</button>
              <button style={styles.btnGhost} type="button" onClick={() => { resetForm(); setShowForm(false); }}>Cancel</button>
            </form>
          </div>
        )}

        {/* Jobs Table */}
        <div style={styles.tableCard}>
          <h2 style={styles.sectionTitle}>Active Jobs</h2>
          {jobs.length === 0 ? (
            <p style={{color: '#888', textAlign: 'center', padding: '40px'}}>No jobs yet. Click "New Job" to get started.</p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr style={styles.thead}>
                  <th style={styles.th}>Customer</th>
                  <th style={styles.th}>Job #</th>
                  <th style={styles.th}>Contract</th>
                  <th style={styles.th}>Collected</th>
                  <th style={styles.th}>Balance Due</th>
                  <th style={styles.th}>Margin</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job, i) => (
                  <tr key={job.id} style={{background: i % 2 === 0 ? '#fff' : '#f9f9f9'}}>
                    <td style={styles.td}><strong>{job.customer_name}</strong><br/><span style={{fontSize:'12px',color:'#888'}}>{job.job_address}</span></td>
                    <td style={styles.td}>{job.job_number || '—'}</td>
                    <td style={styles.td}>{fmt(parseFloat(job.contract_amount) + parseFloat(job.total_cos || 0))}</td>
                    <td style={{...styles.td, color: '#1E7145'}}>{fmt(job.total_paid)}</td>
                    <td style={{...styles.td, color: getBalance(job) > 0 ? '#C0392B' : '#1E7145', fontWeight: 'bold'}}>{fmt(getBalance(job))}</td>
                    <td style={styles.td}>{getMargin(job)}</td>
                    <td style={styles.td}><span style={{...styles.badge, background: job.status === 'active' ? '#E8F5E9' : '#eee', color: job.status === 'active' ? '#1E7145' : '#666'}}>{job.status}</span></td>
                    <td style={styles.td}><button onClick={() => navigate(`/jobs/${job.id}`)} style={styles.btnSmall}>View →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}

const styles = {
  topbar: { background: '#fff', padding: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e6e9ed' },
  pageTitle: { color: '#2E4057', margin: 0, fontSize: '22px' },
  body: { padding: '0 0 32px' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', padding: '24px 32px 0' },
  kpiCard: { background: '#fff', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  kpiLabel: { fontSize: '12px', color: '#888', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  kpiValue: { fontSize: '24px', fontWeight: 'bold', color: '#2E4057' },
  formCard: { margin: '24px 32px 0', background: '#fff', borderRadius: '10px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' },
  tableCard: { margin: '24px 32px', background: '#fff', borderRadius: '10px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  sectionTitle: { margin: '0 0 16px 0', color: '#2E4057', fontSize: '18px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: '#f4f6f9' },
  th: { padding: '10px 12px', textAlign: 'left', fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' },
  td: { padding: '12px', fontSize: '14px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' },
  badge: { padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
  input: { padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px' },
  btnPrimary: { padding: '10px 20px', background: '#2E4057', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' },
  btnGhost: { padding: '10px 20px', background: 'transparent', color: '#888', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer' },
  btnSmall: { padding: '6px 14px', background: '#2E4057', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 100, marginTop: '4px', maxHeight: '260px', overflowY: 'auto' },
  dropItem: { padding: '10px 12px', cursor: 'pointer', fontSize: '14px', borderBottom: '1px solid #f4f4f4' },
  dropCreate: { padding: '10px 12px', cursor: 'pointer', fontSize: '14px', color: '#2E4057', fontWeight: 'bold', background: '#f9fafb' },
};